import { useAuth } from "@clerk/clerk-react";
import { useAction } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../convex/_generated/api";

// Explicit return type of verifyMyPolarEntitlement (mirrors polarEntitlementVerify.ts).
// Cannot use ReturnType<typeof api.xxx> — Convex api references are FunctionReferences,
// not callable functions, so ReturnType<> would fail the constraint check.
type VerifyResult = {
  ok: boolean;
  hasAccess: boolean;
  tier: "free" | "starter" | "plus" | "pro" | "enterprise";
  status: "active" | "canceled" | "past_due" | "trialing" | "none";
  polarSubscriptionId: string | null;
  currentPeriodEnd: number | null;
  source: "polar_api" | "convex_cache";
  cachedAt: number | null;
  expiresAt: number | null;
  skipped?: boolean;
  reason?: string;
};

export type { VerifyResult as EntitlementVerifyResult };

type UseEntitlementVerifyOptions = {
  /**
   * If true, does not auto-fire verifyMyPolarEntitlement on mount.
   * Call verify() manually instead. Default: false.
   */
  skipOnMount?: boolean;
};

export type UseEntitlementVerifyReturn = {
  /** True while the Polar.sh API action is in-flight */
  isVerifying: boolean;
  /** The result of the last verification. null before first verify and when not signed in. */
  result: VerifyResult | null;
  /** Error message if verification threw an unexpected error */
  error: string | null;
  /** Manually trigger a fresh Polar.sh entitlement verification */
  verify: () => void;
};

/**
 * useEntitlementVerify
 *
 * Fires `verifyMyPolarEntitlement` on mount to perform a direct Polar.sh API
 * entitlement check backed by a Convex TTL cache (default 5 minutes, driven by
 * POLAR_SYNC_INTERVAL_MINUTES). The result is stored in the
 * `polarEntitlementCache` table for use by server-side checks that need a
 * stronger freshness guarantee than the webhook-populated Convex state.
 *
 * ## Primary use cases
 * - **Billing success pages** — fire immediately after Polar checkout redirects
 *   so the entitlement cache is warm before the user navigates to a premium route.
 * - **Server provisioning confirmation** — confirm subscription is active via
 *   Polar API directly before allocating expensive server resources.
 * - **Admin entitlement debugging** — surface the raw Polar.sh status alongside
 *   the Convex-stored state.
 *
 * ## Relationship to SubscriptionGate
 * The `SubscriptionGate` reads from `billing.getMyEntitlement` (Convex-stored,
 * webhook-populated state). This hook provides a supplemental Polar.sh API
 * confirmation for components that need it. The TTL cache prevents hammering
 * the Polar API on every render.
 *
 * ## Auth behaviour
 * Only fires when the user is signed in to Clerk. If the user is not signed in,
 * `result` stays null and `verify()` is a no-op.
 *
 * @example
 * ```tsx
 * // On billing success page — fire immediately on mount to warm the cache
 * function BillingSuccessPage() {
 *   const { isVerifying } = useEntitlementVerify();
 *   ...
 * }
 *
 * // Manual trigger only (e.g. a "Refresh subscription" button)
 * function RefreshEntitlementButton() {
 *   const { isVerifying, verify, result } = useEntitlementVerify({ skipOnMount: true });
 *   return <button onClick={verify} disabled={isVerifying}>Refresh</button>;
 * }
 * ```
 */
export function useEntitlementVerify(
  opts: UseEntitlementVerifyOptions = {}
): UseEntitlementVerifyReturn {
  const { isSignedIn } = useAuth();
  const verifyAction = useAction(api.polarEntitlementVerify.verifyMyPolarEntitlement);

  const [isVerifying, setIsVerifying] = useState(false);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prevent double-firing on React StrictMode double-invocation in development
  const hasFired = useRef(false);

  const verify = useCallback(() => {
    if (!isSignedIn) return;

    setIsVerifying(true);
    setError(null);

    verifyAction({})
      .then((res) => {
        // null means the user is not authenticated — do not overwrite a stale result
        if (res !== null) setResult(res);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        setIsVerifying(false);
      });
  }, [isSignedIn, verifyAction]);

  useEffect(() => {
    if (opts.skipOnMount || !isSignedIn || hasFired.current) return;
    hasFired.current = true;
    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn]);

  return { isVerifying, result, error, verify };
}
